import React, { useRef, useState, useEffect } from 'react';
import { CaretDoubleLeft, CaretDoubleRight } from '@phosphor-icons/react';

interface HorizontalScrollContainerProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  showArrows?: boolean;
  arrowClassName?: string;
}

const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

const smoothScrollTo = (element: HTMLElement, target: number, duration: number = 600) => {
  const start = element.scrollLeft;
  const distance = target - start;
  const startTime = performance.now();

  const animateScroll = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easeProgress = easeInOutQuad(progress);
    element.scrollLeft = start + distance * easeProgress;

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  };

  requestAnimationFrame(animateScroll);
};

const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  children,
  className = '',
  contentClassName = '',
  showArrows = true,
  arrowClassName = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const dragScrollLeftRef = useRef(0);
  const updateScrollButtons = () => {
    const container = containerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  };

  const scrollLeft = () => {
    const container = containerRef.current;
    if (container) {
      smoothScrollTo(container, 0, 600); // Scroll to the start
    }
  };

  const scrollRight = () => {
    const container = containerRef.current;
    if (container) {
      const target = container.scrollWidth - container.clientWidth;
      smoothScrollTo(container, target, 600); // Scroll to the end
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const checkAndUpdate = () => {
      updateScrollButtons();
    };
    checkAndUpdate();
    const timer1 = setTimeout(checkAndUpdate, 100);
    const timer2 = setTimeout(checkAndUpdate, 500);
    container.addEventListener('scroll', updateScrollButtons, { passive: true });
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(updateScrollButtons, 50);
    });
    resizeObserver.observe(container);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      container.removeEventListener('scroll', updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(updateScrollButtons, 200);
    return () => clearTimeout(timer);
  }, [children]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    isDownRef.current = true;
    startXRef.current = e.pageX - containerRef.current.offsetLeft;
    dragScrollLeftRef.current = containerRef.current.scrollLeft;
    containerRef.current.classList.add('active:cursor-grabbing');
  };

  const handleMouseLeaveOrUp = () => {
    isDownRef.current = false;
    if (containerRef.current) {
      containerRef.current.classList.remove('active:cursor-grabbing');
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDownRef.current || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1; // scroll-speed
    containerRef.current.scrollLeft = dragScrollLeftRef.current - walk;
  };

  return (
    <div className={`relative ${className}`}>
      <div
        ref={containerRef}
        className={`overflow-x-auto scrollbar-hide w-full cursor-grab select-none ${contentClassName}`}
        onScroll={updateScrollButtons}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeaveOrUp}
        onMouseUp={handleMouseLeaveOrUp}
        onMouseMove={handleMouseMove}
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </div>
      {showArrows && canScrollLeft && (
        <button
          onClick={scrollLeft}
          className={`absolute left-1 top-1/2 -translate-y-1/2 z-20 
          hover:shadow-lg rounded-full p-2 transition-all duration-300 ease-in-out ${arrowClassName}`}
          aria-label='Scroll left'
        >
          <CaretDoubleLeft size={20} className='text-gray-400' weight='bold' />
        </button>
      )}
      {showArrows && canScrollRight && (
        <button
          onClick={scrollRight}
          className={`absolute right-1 top-1/2 -translate-y-1/2 z-20 
          hover:shadow-lg rounded-full p-2 transition-all duration-300 ease-in-out ${arrowClassName}`}
          aria-label='Scroll right'
        >
          <CaretDoubleRight size={20} weight='bold' className='text-gray-400' />
        </button>
      )}
      {showArrows && (
        <>
          {canScrollLeft && (
            <div className='absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white/80 to-transparent pointer-events-none z-10' />
          )}
          {canScrollRight && (
            <div className='absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white/80 to-transparent pointer-events-none z-10' />
          )}
        </>
      )}
    </div>
  );
};

export default HorizontalScrollContainer;
